import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { Fragment } from "react";
import { RichText } from "@payloadcms/richtext-lexical/react";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import Image from "next/legacy/image";
import Link from "next/link";
import SubpageShell from "components/common/SubpageShell";
import {
  getBlogPost,
  getBlogPostSlugs,
  getBlogSettings,
  type BlogPost,
  type BlogSettings
} from "lib/cms/blog";
import {
  CMS_REVALIDATE_SECONDS,
  serverSideCmsTranslations
} from "lib/cms/translations";
import styles from "../../styles/blog/Blog.module.css";

interface BlogPostPageProps {
  post: BlogPost;
  settings: BlogSettings;
  locale: string;
}

const formatDate = (date: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  }).format(new Date(date));

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const slugs = await getBlogPostSlugs();
  const paths = slugs.flatMap((slug) =>
    (locales ?? ["de"]).map((locale) => ({ params: { slug }, locale }))
  );

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps<BlogPostPageProps> = async ({
  locale,
  params
}) => {
  const pageLocale = locale ?? "de";
  const slug = params?.slug as string;
  const post = await getBlogPost(slug, pageLocale);

  if (!post) {
    // Without revalidate, a 404 rendered while the post is still a draft
    // would be cached until the next deploy.
    return {
      notFound: true,
      revalidate: CMS_REVALIDATE_SECONDS
    };
  }

  return {
    props: {
      locale: pageLocale,
      post,
      settings: await getBlogSettings(pageLocale),
      ...(await serverSideCmsTranslations(pageLocale, ["common"]))
    },
    revalidate: CMS_REVALIDATE_SECONDS
  };
};

const BlogPostPage: NextPage<BlogPostPageProps> = ({
  post,
  settings,
  locale
}) => (
  <SubpageShell
    title={`${post.title} — Browserbite`}
    description={post.excerpt}
    path={`/blog/${post.slug}`}
    eyebrow={post.category ?? settings.eyebrow}
    heading={post.title}
    subtitle={post.excerpt}
    heroImage={post.heroImagePath}
    doAnimation
  >
    <div className={`${styles.container} ${styles.postContainer}`}>
      <Link href="/blog" className={styles.backLink}>
        ← {settings.backToOverviewLabel}
      </Link>

      <article className={styles.article}>
        <header className={styles.articleHeader}>
          <div className={styles.articleMeta}>
            <span>{formatDate(post.publishedAt, locale)}</span>
            <span>{post.author}</span>
            {post.readingTime && <span>{post.readingTime}</span>}
          </div>
        </header>

        {post.content.map((block) => (
          <Fragment key={block.id}>
            {block.text && (
              <RichText
                data={block.text as SerializedEditorState}
                className={styles.richText}
              />
            )}
            {block.image && (
              <figure className={styles.contentImage}>
                {block.image.width && block.image.height ? (
                  <div className={styles.contentImageBody}>
                    <Image
                      src={block.image.path}
                      width={block.image.width}
                      height={block.image.height}
                      layout="responsive"
                      sizes="(max-width: 900px) 100vw, 840px"
                      alt={block.image.alt ?? ""}
                    />
                  </div>
                ) : (
                  <div className={styles.contentImageFrame}>
                    <Image
                      src={block.image.path}
                      layout="fill"
                      objectFit="cover"
                      sizes="(max-width: 900px) 100vw, 840px"
                      alt={block.image.alt ?? ""}
                    />
                  </div>
                )}
                {block.image.caption && (
                  <figcaption className={styles.contentImageCaption}>
                    {block.image.caption}
                  </figcaption>
                )}
              </figure>
            )}
          </Fragment>
        ))}
      </article>
    </div>
  </SubpageShell>
);

export default BlogPostPage;
